import { utils, writeFileXLSX } from "xlsx";
import * as XLSX from "xlsx";
import DateTimeUtils from "./DateTimeUtils";
import { CONSTANT_COMMON } from "@/constants";

export default class ExportUtils {
    public export2Word = (element, filename = "") => {
        var preHtml =
            "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Export HTML To Doc</title><style>.pagebreak {page-break-before:always}</style></head><body>";
        var postHtml = "</body></html>";
        var html = preHtml + window.document.getElementById(element)?.innerHTML + postHtml;

        var blob = new Blob(["\ufeff", html], {
            type: "application/msword",
        });

        // Specify link url
        var url = "data:application/vnd.ms-word;charset=utf-8," + encodeURIComponent(html);

        // Specify file name
        filename = filename ? filename + ".doc" : "document.doc";

        // Create download link element
        var downloadLink = document.createElement("a");

        document.body.appendChild(downloadLink);

        if ((window.navigator as any).msSaveOrOpenBlob) {
            (window.navigator as any).msSaveOrOpenBlob(blob, filename);
        } else {
            // Create a link to the file
            downloadLink.href = url;

            // Setting the file name
            downloadLink.download = filename;

            //triggering the function
            downloadLink.click();
        }

        document.body.removeChild(downloadLink);
    };
    public export2Xls = (tableID, filename = "") => {
        var downloadLink;
        var dataType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        var tableSelect = document.getElementById(tableID);
        var tableHTML = tableSelect?.outerHTML.replace(/ /g, "%20");

        // Specify file name
        filename = filename ? filename + ".xlsx" : "excel_data.xlsx";

        // Create download link element
        downloadLink = document.createElement("a");

        document.body.appendChild(downloadLink);

        if ((window.navigator as any).msSaveOrOpenBlob && tableHTML) {
            var blob = new Blob(["\ufeff", tableHTML], {
                type: dataType,
            });
            (window.navigator as any).msSaveOrOpenBlob(blob, filename);
        } else {
            // Create a link to the file
            downloadLink.href = "data:" + dataType + ", " + tableHTML;

            // Setting the file name
            downloadLink.download = filename;

            //triggering the function
            downloadLink.click();
        }
    };
    public export2XLSX = (tableID, filename = "qis_dae") => {
        const data = document.getElementById(tableID);
        const wb = utils.table_to_book(data, { raw: true });
        writeFileXLSX(wb, filename + ".xlsx");
    };

    public export2CSV = (tableID, filename = "qis_dae") => {
        // Get the HTML table element
        const table = document.getElementById(tableID);

        // Convert HTML table to worksheet
        const ws = XLSX.utils.table_to_sheet(table);

        // Convert worksheet to CSV format
        const csv = XLSX.utils.sheet_to_csv(ws);

        // Create a Blob with the CSV data
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

        // Create a download link and trigger a click event to download the file
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename + ".csv";
        link.click();
    };

    public exportJSONToXLSX = (data: any[], filename = "qis_dae") => {
        if (!data || data.length === 0) {
            alert("No data found");
            return;
        }

        const worksheet = utils.json_to_sheet(data);
        const workbook = utils.book_new();

        utils.book_append_sheet(workbook, worksheet, "Sheet1");

        writeFileXLSX(workbook, filename + ".xlsx");
    };

    /**
     * Export a list of records with a merged title row at the top.
     * Filters out non-exportable columns and respects exportRender.
     */
    public exportListWithTitle = (params: {
        data: any[];
        columns: any[];
        sheetName?: string;
        fileNamePrefix: string;
        title?: string;
    }) => {
        const { data, columns, sheetName, fileNamePrefix, title } = params;
        if (!data || data.length === 0) return;

        const exportColumns = columns.filter((col: any) => col.exportable !== false);

        const exportData = data.map((record: any, index: number) => {
            const row: any = {};
            exportColumns.forEach((col: any) => {
                row[col.title] = col.exportRender
                    ? col.exportRender(undefined, record, index)
                    : record[col.dataIndex] ?? "";
            });
            return row;
        });

        const defaultTitle = fileNamePrefix.replace(/_/g, " ");
        const reportTitle = title || defaultTitle;

        // create empty sheet
        const ws = utils.aoa_to_sheet([]);

        // add title row
        utils.sheet_add_aoa(ws, [[reportTitle]], { origin: "A1" });

        // add table starting row 3
        utils.sheet_add_json(ws, exportData, { origin: "A3", skipHeader: false });

        const totalCols = exportColumns.length;
        ws["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
        ];

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, sheetName || fileNamePrefix.replace(/_/g, " "));

        const fileName = `${fileNamePrefix}_${new DateTimeUtils().formatDateTimeSecond(
            new Date(),
            CONSTANT_COMMON.DATE_FORMAT_DATABASE
        )}.xlsx`;
        writeFileXLSX(wb, fileName);
    };

    /**
     * Export grouped records with a merged title row and a bold group heading
     * row above each group's records.
     *
     * groups: [{ title: <group heading>, rows: [ { <dataKey>: value, ... } ] }]
     * columns: [{ title: <column heading>, dataKey: <row field> }]
     */
    public exportGroupedListWithTitle = (params: {
        groups: { title: string; rows: any[] }[];
        columns: { title: string; dataKey: string }[];
        sheetName?: string;
        fileNamePrefix: string;
        title?: string;
    }) => {
        const { groups, columns, sheetName, fileNamePrefix, title } = params;
        if (!groups || !groups.length || !columns || !columns.length) return;

        const totalCols = columns.length;
        const reportTitle = title || fileNamePrefix.replace(/_/g, " ");

        const aoa: any[][] = [];
        aoa.push([reportTitle]);
        aoa.push([]);
        aoa.push(columns.map((col) => col.title));
        const headerRowIndex = 2;

        const groupHeaderRows: number[] = [];
        groups.forEach((group) => {
            groupHeaderRows.push(aoa.length);
            aoa.push([group.title, ...Array(totalCols - 1).fill("")]);
            (group.rows || []).forEach((row: any) => {
                aoa.push(columns.map((col) => row[col.dataKey] ?? ""));
            });
        });

        const ws = utils.aoa_to_sheet(aoa);

        ws["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
            ...groupHeaderRows.map((r) => ({ s: { r, c: 0 }, e: { r, c: totalCols - 1 } })),
        ];

        const bold = { font: { bold: true } };
        const applyBold = (r: number, c: number) => {
            const addr = utils.encode_cell({ r, c });
            if (ws[addr]) {
                (ws[addr] as any).s = bold;
            }
        };
        for (let c = 0; c < totalCols; c++) {
            applyBold(0, c);
            applyBold(headerRowIndex, c);
        }
        groupHeaderRows.forEach((r) => applyBold(r, 0));

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, sheetName || fileNamePrefix.replace(/_/g, " "));

        const fileName = `${fileNamePrefix}_${new DateTimeUtils().formatDateTimeSecond(
            new Date(),
            CONSTANT_COMMON.DATE_FORMAT_DATABASE
        )}.xlsx`;
        writeFileXLSX(wb, fileName);
    };
}
